/**
 * Real-time System Constants
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralized constants for the real-time WebSocket system.
 * Defines connection states, event types, configuration, and error codes.
 *
 * This file provides:
 * - Connection states and statuses
 * - WebSocket event types
 * - Reconnection configuration
 * - Message types
 * - Error codes
 * - i18n keys
 *
 * Design Principles:
 * ------------------
 * - Single source of truth for real-time configuration
 * - Type-safe constants using 'as const'
 * - Organized by category
 * - Well-documented with usage examples
 *
 * Usage:
 * ------
 * ```typescript
 * import { CONNECTION_STATUS, SOCKET_EVENT, REALTIME_CONFIG } from 'core/realtime';
 *
 * if (status === CONNECTION_STATUS.CONNECTED) {
 *   // Handle connected state
 * }
 * ```
 * ============================================================================
 */

/**
 * Connection Status
 *
 * Represents the current state of the WebSocket connection.
 */
export const CONNECTION_STATUS = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
  CLOSING: 'closing',
} as const;

/**
 * Socket Events
 *
 * WebSocket lifecycle and system events.
 */
export const SOCKET_EVENT = {
  // Connection events
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  ERROR: 'error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',

  // Message events
  MESSAGE: 'message',
  PING: 'ping',
  PONG: 'pong',

  // Auth events
  AUTHENTICATE: 'authenticate',
  AUTHENTICATED: 'authenticated',
  UNAUTHORIZED: 'unauthorized',
} as const;

/**
 * Business Events
 *
 * Application-specific real-time events.
 */
export const BUSINESS_EVENT = {
  // Policy events
  POLICY_CREATED: 'policy:created',
  POLICY_UPDATED: 'policy:updated',
  POLICY_DELETED: 'policy:deleted',
  POLICY_APPROVED: 'policy:approved',
  POLICY_REJECTED: 'policy:rejected',

  // Claim events
  CLAIM_CREATED: 'claim:created',
  CLAIM_UPDATED: 'claim:updated',
  CLAIM_DELETED: 'claim:deleted',
  CLAIM_APPROVED: 'claim:approved',
  CLAIM_REJECTED: 'claim:rejected',
  CLAIM_STATUS_CHANGED: 'claim:status_changed',

  // Customer events
  CUSTOMER_CREATED: 'customer:created',
  CUSTOMER_UPDATED: 'customer:updated',
  CUSTOMER_DELETED: 'customer:deleted',

  // User events
  USER_ONLINE: 'user:online',
  USER_OFFLINE: 'user:offline',
  USER_TYPING: 'user:typing',

  // Notification events
  NOTIFICATION: 'notification',
  NOTIFICATION_READ: 'notification:read',
  NOTIFICATION_DELETED: 'notification:deleted',

  // System events
  SYSTEM_ALERT: 'system:alert',
  SYSTEM_MAINTENANCE: 'system:maintenance',
} as const;

/**
 * Message Types
 *
 * Types of messages sent/received through WebSocket.
 */
export const MESSAGE_TYPE = {
  EVENT: 'event',
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',
  PING: 'ping',
  PONG: 'pong',
  ERROR: 'error',
  ACK: 'ack',
} as const;

/**
 * Real-time Configuration
 *
 * WebSocket connection and behavior settings.
 */
export const REALTIME_CONFIG = {
  // Connection
  AUTO_CONNECT: true,
  AUTO_RECONNECT: true,
  RECONNECT_INTERVAL: 5000, // 5 seconds
  MAX_RECONNECT_ATTEMPTS: 10,
  CONNECTION_TIMEOUT: 10000, // 10 seconds

  // Heartbeat (keep-alive)
  ENABLE_HEARTBEAT: true,
  HEARTBEAT_INTERVAL: 30000, // 30 seconds
  HEARTBEAT_TIMEOUT: 5000, // 5 seconds

  // Message handling
  MESSAGE_QUEUE_SIZE: 100,
  ENABLE_MESSAGE_QUEUE: true,
  RETRY_FAILED_MESSAGES: true,
  MAX_MESSAGE_RETRIES: 3,

  // Performance
  ENABLE_COMPRESSION: true,
  MAX_PAYLOAD_SIZE: 1024 * 1024, // 1 MB

  // Debug
  DEBUG_MODE: process.env.NODE_ENV === 'development',
  LOG_MESSAGES: process.env.NODE_ENV === 'development',
} as const;

/**
 * WebSocket URLs
 *
 * WebSocket endpoint configuration.
 */
export const WEBSOCKET_URLS = {
  DEVELOPMENT: 'http://localhost:3000',
  STAGING: 'https://staging-api.bimanova.com',
  PRODUCTION: 'https://api.bimanova.com',
} as const;

/**
 * Get WebSocket URL based on environment
 */
export const getWebSocketUrl = (): string => {
  const env = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

  switch (env) {
    case 'production':
      return WEBSOCKET_URLS.PRODUCTION;
    case 'staging':
      return WEBSOCKET_URLS.STAGING;
    default:
      return WEBSOCKET_URLS.DEVELOPMENT;
  }
};

/**
 * Subscription Channels
 *
 * Channels for subscribing to specific data streams.
 */
export const CHANNEL = {
  // User-specific
  USER: 'user:{userId}',
  USER_NOTIFICATIONS: 'user:{userId}:notifications',

  // Entity-specific
  POLICY: 'policy:{policyId}',
  POLICIES_LIST: 'policies',
  CLAIM: 'claim:{claimId}',
  CLAIMS_LIST: 'claims',
  CUSTOMER: 'customer:{customerId}',
  CUSTOMERS_LIST: 'customers',

  // Global
  GLOBAL: 'global',
  SYSTEM: 'system',
  BROADCAST: 'broadcast',
} as const;

/**
 * Priority Levels
 *
 * Message priority for processing order.
 */
export const PRIORITY = {
  LOW: 0,
  NORMAL: 1,
  HIGH: 2,
  URGENT: 3,
} as const;

/**
 * Error Codes
 *
 * Real-time specific error codes.
 */
export const REALTIME_ERROR_CODES = {
  CONNECTION_FAILED: 'REALTIME_CONNECTION_FAILED',
  CONNECTION_TIMEOUT: 'REALTIME_CONNECTION_TIMEOUT',
  CONNECTION_CLOSED: 'REALTIME_CONNECTION_CLOSED',
  AUTHENTICATION_FAILED: 'REALTIME_AUTH_FAILED',
  SUBSCRIPTION_FAILED: 'REALTIME_SUBSCRIPTION_FAILED',
  MESSAGE_SEND_FAILED: 'REALTIME_MESSAGE_SEND_FAILED',
  INVALID_MESSAGE: 'REALTIME_INVALID_MESSAGE',
  RECONNECT_FAILED: 'REALTIME_RECONNECT_FAILED',
  HEARTBEAT_TIMEOUT: 'REALTIME_HEARTBEAT_TIMEOUT',
  UNSUPPORTED_BROWSER: 'REALTIME_UNSUPPORTED_BROWSER',
} as const;

/**
 * Storage Keys
 *
 * LocalStorage keys for real-time state persistence.
 */
export const REALTIME_STORAGE_KEYS = {
  SUBSCRIPTIONS: 'bimanova_realtime_subscriptions',
  PENDING_MESSAGES: 'bimanova_realtime_pending_messages',
  CONNECTION_STATE: 'bimanova_realtime_connection_state',
} as const;

/**
 * i18n Keys
 *
 * Translation key prefixes for real-time messages.
 */
export const REALTIME_I18N_KEYS = {
  CONNECTION: 'realtime:connection',
  ERRORS: 'realtime:errors',
  EVENTS: 'realtime:events',
  STATUS: 'realtime:status',
} as const;

/**
 * Event Notification Keys
 *
 * Centralized i18n keys for business event notifications.
 */
export const EVENT_NOTIFICATION_KEYS = {
  // Policy events
  POLICY_CREATED: {
    TITLE: 'realtime:events.policy_created_title',
    MESSAGE: 'realtime:events.policy_created_message',
  },
  POLICY_UPDATED: {
    TITLE: 'realtime:events.policy_updated_title',
    MESSAGE: 'realtime:events.policy_updated_message',
  },
  POLICY_DELETED: {
    TITLE: 'realtime:events.policy_deleted_title',
    MESSAGE: 'realtime:events.policy_deleted_message',
  },
  POLICY_APPROVED: {
    TITLE: 'realtime:events.policy_approved_title',
    MESSAGE: 'realtime:events.policy_approved_message',
  },

  // Claim events
  CLAIM_CREATED: {
    TITLE: 'realtime:events.claim_created_title',
    MESSAGE: 'realtime:events.claim_created_message',
  },
  CLAIM_UPDATED: {
    TITLE: 'realtime:events.claim_updated_title',
    MESSAGE: 'realtime:events.claim_updated_message',
  },
  CLAIM_APPROVED: {
    TITLE: 'realtime:events.claim_approved_title',
    MESSAGE: 'realtime:events.claim_approved_message',
  },
  CLAIM_REJECTED: {
    TITLE: 'realtime:events.claim_rejected_title',
    MESSAGE: 'realtime:events.claim_rejected_message',
  },
  CLAIM_STATUS_CHANGED: {
    TITLE: 'realtime:events.claim_status_changed_title',
    MESSAGE: 'realtime:events.claim_status_changed_message',
  },

  // Customer events
  CUSTOMER_CREATED: {
    TITLE: 'realtime:events.customer_created_title',
    MESSAGE: 'realtime:events.customer_created_message',
  },
  CUSTOMER_UPDATED: {
    TITLE: 'realtime:events.customer_updated_title',
    MESSAGE: 'realtime:events.customer_updated_message',
  },

  // Notification events
  NOTIFICATION: {
    TITLE: 'realtime:events.notification_title',
    MESSAGE: 'realtime:events.notification_message',
  },

  // System events
  SYSTEM_ALERT: {
    TITLE: 'realtime:events.system_alert_title',
    MESSAGE: 'realtime:events.system_alert_message',
  },
  SYSTEM_MAINTENANCE: {
    TITLE: 'realtime:events.system_maintenance_title',
    MESSAGE: 'realtime:events.system_maintenance_message',
  },
} as const;

/**
 * Default notification durations (in milliseconds)
 */
export const EVENT_NOTIFICATION_DURATION = {
  DEFAULT: 5000,
  SYSTEM_ALERT: 10000,
  SYSTEM_MAINTENANCE: 15000,
} as const;

/**
 * Close Codes
 *
 * WebSocket close codes (RFC 6455).
 */
export const CLOSE_CODE = {
  NORMAL_CLOSURE: 1000,
  GOING_AWAY: 1001,
  PROTOCOL_ERROR: 1002,
  UNSUPPORTED_DATA: 1003,
  NO_STATUS_RECEIVED: 1005,
  ABNORMAL_CLOSURE: 1006,
  INVALID_FRAME_PAYLOAD: 1007,
  POLICY_VIOLATION: 1008,
  MESSAGE_TOO_BIG: 1009,
  INTERNAL_ERROR: 1011,
  SERVICE_RESTART: 1012,
  TRY_AGAIN_LATER: 1013,
} as const;

/**
 * Ready States
 *
 * WebSocket ready states.
 */
export const READY_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const;
